/* global DeepLinkFavoriting, FavoritesEnabled */
/* eslint-disable import/no-unused-modules */
import _ from 'lodash';
import {
  helperGetApiAccountRoute,
  helperGetApiHeaders,
  helperGetFavoriteDetailsApiRoute,
} from 'helpers/ApiHelper';
import { helperGetAccountUrl } from 'helpers/RouteHelper';
import {
  setAnonymousFavoritesInStorage,
  getAnonymousFavoritesFromStorage,
  ANONYMOUS_ACCOUNT_TYPE,
} from 'helpers/AnonymousHelper';
import { getFavorites, setFavorites, deleteFavorites, getAccountCookie } from 'helpers/Account';
import { setLocalStorageItem, getLocalStorageItem, removeLocalStorageItem } from 'helpers/Storage';
import {
  FAVORITES_TYPE_PERSONALITY,
  getAccountAnalyticsEvents,
  PageNames,
  SegmentSources,
  tealiumEventPageTypes,
  TrackEvents,
} from '@foxcorp/delta-web-analytics';
import { userAttributeEvents } from 'helpers/OptimizelyHelper';

export const helperGetPerks = () => _.get(window.ExternalConfig, 'profile.perks');
const FAVORITES_STORAGE_NAME = 'FOX_FAVORITES';
export const FAVORITE_DOMAINS = ['Nation', 'legacyV1', 'legacyV2', 'Entertainment', 'foxnation'];
const DEEP_LINK_FAVORITE = 'favorite=true';
const DEEP_LINK_FAVORITE_NOT_AUTHED = 'DEEP_LINK_FAVORITED';
const USER_TRAITS_STORAGE = 'ajs_user_traits';

export const getFavoriteRequestOptions = ({ account, favoritableItems, profileFavorites }) => {
  const { accessToken, deviceId } = account;
  const headers = helperGetApiHeaders({ pathKey: 'showsQuery', accessToken, deviceId });
  const route = helperGetApiAccountRoute('favorites');
  const options = { route, headers, favoritableItems, profileFavorites };
  return options;
};

const setFavoritesInStorage = favorites => {
  const stringifiedFavorites = JSON.stringify(favorites);
  setLocalStorageItem({ key: FAVORITES_STORAGE_NAME, value: stringifiedFavorites });
};

export const getFavoritesFromStorage = () => {
  const favorites = getLocalStorageItem({ key: FAVORITES_STORAGE_NAME });
  return JSON.parse(favorites);
};

export const setInitialFavoritesStorage = favorites => {
  const storedFavorites = getLocalStorageItem({ key: FAVORITES_STORAGE_NAME }) || [];
  if (storedFavorites?.length === 0) {
    setFavoritesInStorage(favorites);
  }
};

export const filterByFavoriteType = data =>
  data?.reduce((acc, item) => {
    const [type, id] = item?.id?.split(':');
    if (type?.toLowerCase() === 'series' || type?.toLowerCase() === 'category')
      acc.push({ id, type });
    return acc;
  }, []);

const addFavoriteToStorage = favorite => {
  const { favoritableItems } = favorite;
  const favorites = getFavoritesFromStorage() || [];

  favoritableItems.forEach(favoriteItem => {
    const { id, '@type': type } = favoriteItem;
    const filter = favorites.filter(item => item.favoriteID === id);
    if (!filter.length) favorites.push({ type, id });
  });

  setFavoritesInStorage(favorites);
  return favorites;
};

const removeFavoriteFromStorage = favorite => {
  const { favoritableItems } = favorite;
  let filtered = getFavoritesFromStorage() || [];
  favoritableItems.forEach(favoriteItem => {
    filtered = filtered.filter(item => item.id !== favoriteItem.id);
  });

  setFavoritesInStorage(filtered);
  return filtered;
};

export const getFavoritesDetailsForAccount = (account, favorites) => {
  const favoritesShowIds = favorites.map(favorite => favorite.id);
  const route = helperGetFavoriteDetailsApiRoute(favoritesShowIds);
  const options = { ...getFavoriteRequestOptions({ account }), route };

  return new Promise((resolve, reject) => {
    if (!favoritesShowIds || !favoritesShowIds.length || !route) {
      resolve([]);
      return;
    }

    getFavorites(options)
      .then(result => {
        const items = result?.member ?? [];
        resolve({ items });
      })
      .catch(reject);
  });
};

export const createFavorite = (account, favorite, profileFavorites) => {
  const { favoritableItems } = favorite;
  if (!favoritableItems) return null;
  const { profileId, accountType } = account;
  const { foxProfileState } = userAttributeEvents();
  const { [TrackEvents.ON_FAVORITE]: onFavoriteAnalytics } = getAccountAnalyticsEvents({
    foxProfileState,
    profileId,
  });

  return new Promise((resolve, reject) => {
    const { location, networkId, showID, seriesName, recommendationId } = favorite;
    const isPersonality = favorite.favoriteType === FAVORITES_TYPE_PERSONALITY;
    if (accountType === ANONYMOUS_ACCOUNT_TYPE) {
      const anonymousFavorites = getAnonymousFavoritesFromStorage() || [];
      favoritableItems.forEach(favoriteItem => {
        const filter = anonymousFavorites.filter(item => item.showID === favoriteItem.id);
        if (!filter.length) anonymousFavorites.push(favoriteItem);
      });
      setAnonymousFavoritesInStorage({ anonymousFavorites });
    }

    setFavorites({ favoritableItems, profileFavorites, isPersonality })
      .then(result => {
        addFavoriteToStorage(favorite);
        onFavoriteAnalytics({
          location,
          networkId,
          seriesId: showID,
          seriesName,
          status: 'Yes',
          recommendationId,
        });
        resolve(result);
      })
      .catch(reject);
  });
};

export const deleteFavoritesForAccount = (account, favoriteList, profileFavorites) => {
  const { profileId, accountType } = account;
  const favoritableItems = favoriteList?.reduce((acc, item) => {
    const { favoritableItems } = item;
    favoritableItems.forEach(favItem => {
      const { id, '@type': type } = favItem;
      acc.push({ id: `${type}:${id}` });
    });
    return acc;
  }, []);
  const { foxProfileState } = userAttributeEvents();
  const { [TrackEvents.ON_UNFAVORITE]: onUnfavoriteAnalytics } = getAccountAnalyticsEvents({
    foxProfileState,
    profileId,
  });

  return new Promise((resolve, reject) => {
    if (accountType === ANONYMOUS_ACCOUNT_TYPE) {
      const anonymousFavorites = getAnonymousFavoritesFromStorage() || [];
      let filtered = anonymousFavorites;
      favoritableItems.forEach(favoriteItem => {
        filtered = filtered.filter(item => item.favoriteID !== favoriteItem.id);
      });
      setAnonymousFavoritesInStorage({ anonymousFavorites: filtered });
    }
    deleteFavorites({ favoritableItems, profileFavorites })
      .then(result => {
        favoriteList.forEach(favorite => {
          const { location, networkId, showID, seriesName, recommendationId } = favorite;
          removeFavoriteFromStorage(favorite);
          onUnfavoriteAnalytics({
            location,
            networkId,
            seriesId: showID,
            seriesName,
            status: 'No',
            recommendationId,
          });
        });
        resolve(result);
      })
      .catch(reject);
  });
};

export function deepLinkFavoriteSeries({ props, setDeepLinkFavorited }) {
  const { account } = props;
  const querystring = window.location.search.substring(1);
  const hasFavorite = querystring.includes(DEEP_LINK_FAVORITE);
  const favoriteDeepLinkEnabled = FavoritesEnabled && DeepLinkFavoriting;
  if (hasFavorite && favoriteDeepLinkEnabled) {
    if (!account) {
      // If no account, redirect to account login screen with query string
      // Store favorite info in local storage
      const {
        params: { showId: showID },
        seriesData,
        recommendationId,
      } = props;
      const network = _.get(seriesData, 'network');
      const seriesName = _.get(seriesData, 'name');
      const favoriteData = {
        showID,
        source: SegmentSources.DETAIL,
        location: _.get(tealiumEventPageTypes, PageNames.FAVORITES_DETAIL),
        networkId: network,
        seriesName,
        recommendationId,
        seriesId: showID,
      };
      setLocalStorageItem({
        key: DEEP_LINK_FAVORITE_NOT_AUTHED,
        value: JSON.stringify(favoriteData),
      });
      // Redirect to account page
      window.location = `${helperGetAccountUrl()}`;
      return null;
    }
    // If account, add show to users favorites
    return setDeepLinkFavorited(true);
  }
  return null;
}

export function getUserTraits() {
  try {
    const userTraits = getLocalStorageItem({ key: USER_TRAITS_STORAGE });
    return JSON.parse(userTraits) || {};
  } catch {
    return {};
  }
}

export function getCommonTealiumProps() {
  const path = window?.location?.pathname;
  let pageName = window?.utag_data?.page_name;
  let pageType = window?.utag_data?.page_type;
  if (path.includes('/watch')) {
    pageName = 'delta:web:player';
    pageType = 'player';
  }
  if (path.includes('/live-news')) {
    pageName = 'delta:web:live tv:landing';
    pageType = 'live tv';
  }
  return {
    pageName,
    pageType,
  };
}

export function checkDeepLinkFavorite() {
  const account = getAccountCookie();
  const profileId = _.get(account, 'profileId');
  const favoriteDeepLinkEnabled = FavoritesEnabled && DeepLinkFavoriting;
  if (favoriteDeepLinkEnabled) {
    const deepLinkFavoriteData = getLocalStorageItem({ key: DEEP_LINK_FAVORITE_NOT_AUTHED });
    if (deepLinkFavoriteData) {
      const favorite = JSON.parse(deepLinkFavoriteData);
      const options = getFavoriteRequestOptions({ account, favorite });
      const { foxProfileState } = userAttributeEvents();
      const { [TrackEvents.ON_FAVORITE]: onFavoriteAnalytics } = getAccountAnalyticsEvents({
        foxProfileState,
        profileId,
      });
      return new Promise((resolve, reject) => {
        setFavorites(options)
          .then(result => {
            addFavoriteToStorage(favorite);
            onFavoriteAnalytics(favorite);
            removeLocalStorageItem({ key: DEEP_LINK_FAVORITE_NOT_AUTHED });
            resolve(result);
          })
          .catch(reject);
      });
    }
  }
  return null;
}

export const accountPathMappings = id => {
  const mappings = {
    eProfile: 'edit-profile',
    changePassword: 'change-password',
    manageFavorites: 'manage-favorites',
    parentalControls: 'parental-controls',
    signOut: 'sign-out',
  };
  return mappings[id] || id;
};
